import React, { createContext, useEffect, startTransition } from 'react'

import { Constants } from '@/assets'
import { initialUserReducerState, UserActions, UserReducer, userReducer, UserTypes } from '@/modules/User'
import {
  initialInsurancesReducerState,
  InsurancesActions,
  InsurancesReducer,
  insurancesReducer,
  InsurancesTypes,
} from '@/modules/Insurances'
import {
  ConfigurationActions,
  ConfigurationReducer,
  configurationReducer,
  ConfigurationTypes,
  initialConfigurationReducerState,
} from '@/modules/Configuration'
import {
  DiscussionsActions,
  DiscussionsReducer,
  discussionsReducer,
  DiscussionsTypes,
  initialDiscussionsReducerState,
} from '@/modules/Discussions'
import {
  initialNotificationsReducerState,
  NotificationsActions,
  NotificationsReducer,
  notificationsReducer,
  NotificationsTypes,
} from '@/modules/Notifications'
import {
  initialApiNotificationsReducerState,
  ApiNotificationsActions,
  ApiNotificationsReducer,
  apiNotificationsReducer,
  ApiNotificationsTypes,
} from '@/modules/ApiNotifications'
import {
  FavoritesActions,
  FavoritesReducer,
  favoritesReducer,
  FavoritesTypes,
  initialFavoritesReducerState,
} from '@/modules/Favorites'
import { CartActions, CartReducer, cartReducer, CartTypes, initialCartReducerState } from '@/modules/Cart'
import {
  PetsitterUserActions,
  PetsitterUserReducer,
  petsitterUserReducer,
  PetsitterUserTypes,
  initialPetsitterUserReducerState,
} from '@/modules/PetsitterUser'

import useCombinedReducers from './useCombinedReducers'
import useLoggedReducer from '@/loggers/useLoggedReducer'
import GlobalLoader from '@/components/GlobalLoader'

const STORAGE_KEY = `${Constants.storageID}/Store`

// Root store
export interface AppContextInterface {
  user: UserReducer
  configuration: ConfigurationReducer
  discussions: DiscussionsReducer
  notifications: NotificationsReducer
  apiNotifications: ApiNotificationsReducer
  insurances: InsurancesReducer
  favorites: FavoritesReducer
  cart: CartReducer
  petsitterUser: PetsitterUserReducer
}

// Combine all reducers actions
type AppActions =
  | UserActions
  | ConfigurationActions
  | DiscussionsActions
  | NotificationsActions
  | ApiNotificationsActions
  | InsurancesActions
  | FavoritesActions
  | CartActions
  | PetsitterUserActions

export type AppDispatch = React.Dispatch<AppActions>

// Create initial state for root store
const initialState: AppContextInterface = {
  user: initialUserReducerState,
  cart: initialCartReducerState,
  configuration: initialConfigurationReducerState,
  discussions: initialDiscussionsReducerState,
  notifications: initialNotificationsReducerState,
  apiNotifications: initialApiNotificationsReducerState,
  insurances: initialInsurancesReducerState,
  favorites: initialFavoritesReducerState,
  petsitterUser: initialPetsitterUserReducerState,
}

const DispatchContext = createContext<AppDispatch>(() => null)
const StateContext = createContext<AppContextInterface>(initialState)

type Props = {
  children?: JSX.Element
  isSeoPublicRoute?: boolean
}

const isServer = typeof window === 'undefined'

const AppProvider: React.FC<Props> = ({ children, isSeoPublicRoute }) => {
  const [isRestored, setRestored] = React.useState(false)

  // Combine all reducers into one
  const [state, dispatch] = useCombinedReducers<AppContextInterface, AppActions>({
    user: useLoggedReducer(userReducer, initialUserReducerState, new RegExp('User/.*')),
    configuration: useLoggedReducer(
      configurationReducer,
      initialConfigurationReducerState,
      new RegExp('Configuration/.*'),
    ),
    discussions: useLoggedReducer(discussionsReducer, initialDiscussionsReducerState, new RegExp('Discussions/.*')),
    notifications: useLoggedReducer(
      notificationsReducer,
      initialNotificationsReducerState,
      new RegExp('Notifications/.*'),
    ),
    apiNotifications: useLoggedReducer(
      apiNotificationsReducer,
      initialApiNotificationsReducerState,
      new RegExp('ApiNotifications/.*'),
    ),
    insurances: useLoggedReducer(insurancesReducer, initialInsurancesReducerState, new RegExp('Insurances/.*')),
    favorites: useLoggedReducer(favoritesReducer, initialFavoritesReducerState, new RegExp('Favorites/.*')),
    cart: useLoggedReducer(cartReducer, initialCartReducerState, new RegExp('Cart/.*')),
    petsitterUser: useLoggedReducer(
      petsitterUserReducer,
      initialPetsitterUserReducerState,
      new RegExp('PetsitterUser/.*'),
    ),
  })

  useEffect(() => {
    if (isServer) return

    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      const existingValue: AppContextInterface | null = raw ? JSON.parse(raw) : null

      if (existingValue?.user) dispatch({ type: UserTypes.Restore, payload: existingValue.user })
      if (existingValue?.configuration)
        dispatch({ type: ConfigurationTypes.Restore, payload: existingValue.configuration })
      if (existingValue?.discussions) dispatch({ type: DiscussionsTypes.Restore, payload: existingValue.discussions })
      if (existingValue?.notifications)
        dispatch({ type: NotificationsTypes.Restore, payload: existingValue.notifications })
      if (existingValue?.apiNotifications)
        dispatch({ type: ApiNotificationsTypes.Restore, payload: existingValue.apiNotifications })
      if (existingValue?.insurances) dispatch({ type: InsurancesTypes.Restore, payload: existingValue.insurances })
      if (existingValue?.favorites) dispatch({ type: FavoritesTypes.Restore, payload: existingValue.favorites })
      if (existingValue?.cart) dispatch({ type: CartTypes.Restore, payload: existingValue.cart })
      if (existingValue?.petsitterUser)
        dispatch({ type: PetsitterUserTypes.Restore, payload: existingValue.petsitterUser })
    } finally {
      // ✅ mark restored *without* forcing sync hydration work
      startTransition(() => setRestored(true))
    }
  }, [])

  useEffect(() => {
    if (!isServer) {
      // Save store to LocalStorage at each modif
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    }
  }, [state])

  // if (isSeoPublicRoute) {
  //   // For SEO public routes, we want to skip the restoration/loading state and render immediately
  //   return <StateContext.Provider value={initialState}>{children}</StateContext.Provider>
  // }

  // Wrap children in Context Providers
  return (
    <DispatchContext.Provider value={dispatch}>
      <StateContext.Provider value={state}>{isRestored ? children : <GlobalLoader />}</StateContext.Provider>
    </DispatchContext.Provider>
  )
}

export { DispatchContext, StateContext, AppProvider }
